import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Clock, Calendar, User } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CTACard from "@/components/blog/CTACard";
import BlogImage from "@/components/blog/BlogImage";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import { getPostBySlug } from "@/data/blogPosts";
const CienciaHabitos = () => {
  const post = getPostBySlug("ciencia-formacao-habitos");

  if (!post) return null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle} />
        <meta name="twitter:description" content={post.metaDescription} />
        <link rel="canonical" href={`https://habitfrog.com/posts/${post.slug}`} />
      </Helmet>

      <Header />
      <ScrollToTopButton />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="mb-8" aria-label="Breadcrumb">
            <Link to="/home" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Voltar ao início
            </Link>
          </nav>

          {/* Article Header */}
          <header className="max-w-4xl mx-auto mb-12">
            <span className="inline-block bg-primary/20 text-primary px-3 py-1 rounded-full text-sm font-semibold mb-4">
              {post.category}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6 leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </header>

          {/* Article Content */}
          <article className="max-w-4xl mx-auto prose prose-lg max-w-none">
            {/* Introdução */}
            <div className="mb-12">
              <p className="text-xl text-muted-foreground leading-relaxed">
                Por que algumas pessoas conseguem manter hábitos saudáveis sem esforço aparente, enquanto outras lutam constantemente contra suas próprias vontades? A resposta está no cérebro. A neurociência moderna nos revela que os hábitos não são simples escolhas conscientes, mas sim padrões neurais profundamente enraizados que moldam nosso comportamento de forma automática. Entender como esse mecanismo funciona é o primeiro passo para hackear seu próprio cérebro e criar mudanças duradouras.
              </p>
            </div>

            {/* Grid de duas colunas - O que são hábitos */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">O que são hábitos, afinal?</h2>
                <p className="text-foreground/80">
                  Hábitos são comportamentos automáticos que executamos sem pensar conscientemente. Estima-se que cerca de <strong>40% a 45% das nossas ações diárias</strong> são hábitos, não decisões deliberadas.
                </p>
                <p className="text-foreground/80 mt-4">
                  Essa automatização existe por uma razão evolutiva: poupar energia mental. Se tivéssemos que pensar conscientemente em cada ação, nosso cérebro entraria em colapso por sobrecarga cognitiva.
                </p>
              </div>
              <div>
                <p className="text-foreground/80">
                  O cérebro desenvolveu a capacidade de transformar sequências repetidas de ações em rotinas automáticas, liberando recursos cognitivos para tarefas mais complexas.
                </p>
                <p className="text-foreground/80 mt-4">
                  Essa característica é uma faca de dois gumes: pode trabalhar a seu favor (quando você cria hábitos saudáveis) ou contra você (quando maus hábitos tomam conta). A boa notícia é que você pode <strong>reprogramar esses padrões</strong>.
                </p>
              </div>
            </div>

            <BlogImage 
              src="/images/blog/cerebro-cientifico.jpg" 
              alt="Modelo científico do cérebro humano mostrando estruturas neurais relacionadas à formação de hábitos e neuroplasticidade" 
              className="h-64 md:h-80 mb-12"
              priority
            />

            {/* O Loop do Hábito */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl">🔄</span>
                O Loop do Hábito: A Arquitetura de Todo Comportamento Automático
              </h2>

              <p className="text-foreground/80 mb-8">
                O neurocientista do MIT, <strong>Ann Graybiel</strong>, e posteriormente o jornalista <strong>Charles Duhigg</strong> em seu bestseller "O Poder do Hábito", popularizaram o conceito do <strong>Loop do Hábito</strong> – um ciclo de três etapas que está por trás de todo comportamento habitual.
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-br from-primary/20 to-primary/5 p-6 rounded-xl border border-primary/30">
                  <div className="text-4xl mb-4">🔔</div>
                  <h3 className="font-bold text-foreground mb-3 text-xl">1. Gatilho (Cue)</h3>
                  <p className="text-sm text-muted-foreground">
                    É o sinal que dispara o comportamento automático. Pode ser um horário específico, uma localização, um estado emocional, outras pessoas ou uma ação precedente.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-accent/20 to-accent/5 p-6 rounded-xl border border-accent/30">
                  <div className="text-4xl mb-4">⚡</div>
                  <h3 className="font-bold text-foreground mb-3 text-xl">2. Rotina (Routine)</h3>
                  <p className="text-sm text-muted-foreground">
                    É o comportamento em si – a ação que você executa automaticamente após o gatilho. Pode ser física, mental ou emocional. É a parte visível do hábito.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-golden/20 to-golden/5 p-6 rounded-xl border border-golden/30">
                  <div className="text-4xl mb-4">🎁</div>
                  <h3 className="font-bold text-foreground mb-3 text-xl">3. Recompensa (Reward)</h3>
                  <p className="text-sm text-muted-foreground">
                    É o benefício que seu cérebro obtém ao completar a rotina. Pode ser prazer, alívio de estresse, satisfação social. A recompensa consolida o loop.
                  </p>
                </div>
              </div>

              <div className="bg-card p-8 rounded-2xl border mb-8">
                <h3 className="text-xl font-bold text-foreground mb-4">📌 Exemplo Prático do Loop</h3>
                <div className="grid md:grid-cols-3 gap-4 text-center">
                  <div className="p-4 bg-secondary/30 rounded-xl">
                    <p className="text-sm text-muted-foreground mb-1">GATILHO</p>
                    <p className="font-semibold text-foreground">Sentir estresse no trabalho</p>
                  </div>
                  <div className="p-4 bg-secondary/30 rounded-xl">
                    <p className="text-sm text-muted-foreground mb-1">ROTINA</p>
                    <p className="font-semibold text-foreground">Comer um chocolate</p>
                  </div>
                  <div className="p-4 bg-secondary/30 rounded-xl">
                    <p className="text-sm text-muted-foreground mb-1">RECOMPENSA</p>
                    <p className="font-semibold text-foreground">Sensação de prazer e alívio</p>
                  </div>
                </div>
                <p className="text-foreground/80 mt-4 text-center">
                  Para mudar esse hábito, você precisa <strong>substituir a rotina</strong> por algo que forneça recompensa similar.
                </p>
              </div>
            </section>

            {/* O Papel dos Gânglios Basais */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl">🧠</span>
                O Papel dos Gânglios Basais: Onde os Hábitos Moram
              </h2>

              <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                <div>
                  <p className="text-foreground/80 mb-4">
                    Pesquisas com neuroimagem revelaram que os hábitos são armazenados em uma região específica do cérebro chamada <strong>gânglios basais</strong>, uma estrutura primitiva localizada na base do cérebro.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Isso explica por que pacientes com amnésia severa ainda conseguem desenvolver novos hábitos. O sistema de hábitos é independente da memória declarativa.
                  </p>
                  <p className="text-foreground/80">
                    Quando um comportamento se torna habitual, a atividade cerebral muda: o córtex pré-frontal "desliga" e os gânglios basais assumem o controle.
                  </p>
                </div>
                <BlogImage 
                  src="/images/blog/ganglios-basais.jpg" 
                  alt="Diagrama anatômico do cérebro destacando os gânglios basais e o córtex pré-frontal responsáveis pelos hábitos" 
                  className="h-64"
                />
              </div>
            </section>

            <div className="my-16"><CTACard variant="primary" /></div>

            {/* O Mito dos 21 Dias */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl">📅</span>
                O Mito dos 21 Dias: Quanto Tempo Realmente Leva?
              </h2>

              <div className="bg-coral/10 p-6 rounded-xl border-l-4 border-coral mb-8">
                <h3 className="font-bold text-foreground mb-2">⚠️ Desmistificando uma Crença Popular</h3>
                <p className="text-foreground/80">
                  Você provavelmente já ouviu que são necessários 21 dias para formar um novo hábito. Essa ideia popular vem de uma interpretação errônea do trabalho do cirurgião plástico Maxwell Maltz nos anos 1960.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">O Que a Ciência Diz</h3>
                  <p className="text-foreground/80 mb-4">
                    Um estudo de 2009 da University College London, liderado por Philippa Lally, acompanhou 96 pessoas tentando formar novos hábitos durante 12 semanas.
                  </p>
                  <p className="text-foreground/80">
                    O resultado? O tempo médio para um comportamento se tornar automático foi de <strong>66 dias</strong> – não 21. Mas a variação era enorme: de 18 a 254 dias, dependendo da pessoa e da complexidade do hábito.
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <h3 className="font-semibold text-foreground mb-4">Fatores que Influenciam o Tempo</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• <strong>Complexidade do hábito:</strong> beber água é mais fácil que fazer 50 flexões</li>
                    <li>• <strong>Frequência:</strong> hábitos diários se formam mais rápido que semanais</li>
                    <li>• <strong>Personalidade:</strong> algumas pessoas têm mais facilidade com rotinas</li>
                    <li>• <strong>Ambiente:</strong> gatilhos consistentes aceleram a formação</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Dopamina e Recompensa */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl">⚡</span>
                O Papel da Dopamina: Por Que Hábitos Viciam
              </h2>

              <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                <BlogImage 
                  src="/images/blog/dopamina-neuronio.jpg" 
                  alt="Representação visual de neurônios liberando dopamina no cérebro durante formação de hábitos" 
                  className="h-64"
                />
                <div>
                  <p className="text-foreground/80 mb-4">
                    A dopamina é frequentemente chamada de "hormônio do prazer", mas essa descrição é imprecisa. A dopamina está mais relacionada à <strong>antecipação de recompensa</strong> do que ao prazer em si.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Quando você está prestes a receber algo bom – seja comida, uma notificação no celular, ou um elogio – seu cérebro libera dopamina. Essa liberação cria desejo e motivação para agir.
                  </p>
                  <p className="text-foreground/80">
                    O mais fascinante é que, com a repetição, a dopamina começa a ser liberada <strong>no momento do gatilho</strong>, não da recompensa. É por isso que só ver o ícone do Instagram pode gerar um "puxão" irresistível.
                  </p>
                </div>
              </div>
            </section>

            {/* Neuroplasticidade */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl">🔧</span>
                Neuroplasticidade: Seu Cérebro Pode Mudar
              </h2>

              <p className="text-foreground/80 mb-8">
                A boa notícia é que seu cérebro não é fixo. O conceito de <strong>neuroplasticidade</strong> revolucionou nossa compreensão do cérebro adulto. Até os anos 1990, acreditava-se que o cérebro era essencialmente "moldado" na infância e permanecia fixo na vida adulta.
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">🔬</div>
                  <h3 className="font-semibold text-foreground mb-3">Estudo dos Taxistas</h3>
                  <p className="text-sm text-muted-foreground">
                    Taxistas de Londres tinham hipocampos maiores que a média – a área responsável pela memória espacial – demonstrando que o uso intensivo molda o cérebro.
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">🎹</div>
                  <h3 className="font-semibold text-foreground mb-3">Músicos e Atletas</h3>
                  <p className="text-sm text-muted-foreground">
                    Pianistas profissionais têm córtex motor mais desenvolvido nas áreas que controlam os dedos. A prática literalmente esculpe o cérebro.
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">🧘</div>
                  <h3 className="font-semibold text-foreground mb-3">Meditadores</h3>
                  <p className="text-sm text-muted-foreground">
                    Pessoas que meditam regularmente mostram aumento na espessura do córtex pré-frontal e mudanças na amígdala (centro emocional).
                  </p>
                </div>
              </div>
            </section>

            {/* Keystone Habits */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl">🗝️</span>
                Hábitos-Chave: O Efeito Dominó da Mudança
              </h2>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <p className="text-foreground/80 mb-4">
                    Nem todos os hábitos são criados iguais. Alguns têm o poder de desencadear uma cascata de mudanças positivas em outras áreas da vida. Charles Duhigg os chamou de <strong>"keystone habits"</strong> (hábitos-chave ou hábitos pedra angular).
                  </p>
                  <p className="text-foreground/80">
                    O exemplo mais estudado é o exercício físico. Pessoas que começam a se exercitar regularmente tendem a comer melhor, dormir mais, fumar menos e até gastar dinheiro de forma mais consciente.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-primary/10 to-accent/10 p-6 rounded-xl border">
                  <h3 className="font-semibold text-foreground mb-4">Exemplos de Hábitos-Chave</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• <strong>Exercício físico:</strong> melhora humor, sono, energia</li>
                    <li>• <strong>Fazer a cama:</strong> cria sensação de ordem e produtividade</li>
                    <li>• <strong>Meditação:</strong> aumenta autoconsciência e controle emocional</li>
                    <li>• <strong>Leitura diária:</strong> estimula curiosidade e aprendizado contínuo</li>
                    <li>• <strong>Jantar em família:</strong> melhora comunicação e saúde emocional</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Conclusão */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">Conclusão: Seu Cérebro Está Esperando Instruções</h2>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <p className="text-foreground/80 mb-4">
                    A ciência dos hábitos nos mostra que não somos prisioneiros de nossos comportamentos. Sim, hábitos são poderosos e automáticos, mas também são <strong>reprogramáveis</strong>.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Seu cérebro está constantemente se adaptando às suas ações e ao seu ambiente. Cada vez que você repete um comportamento, está fortalecendo caminhos neurais específicos.
                  </p>
                  <p className="text-foreground/80">
                    A pergunta não é <em>"tenho força de vontade suficiente?"</em>, mas sim <em>"estou criando o ambiente e os gatilhos certos para que os bons hábitos aconteçam automaticamente?"</em>
                  </p>
                </div>
                <BlogImage 
                  src="/images/blog/equipe-colaborativa.jpg" 
                  alt="Pessoa sorrindo ao completar um hábito positivo, representando sucesso e transformação através da ciência dos hábitos" 
                  className="h-64"
                />
              </div>

              <p className="text-xl font-semibold text-primary text-center py-4">
                Entender a ciência por trás da formação de hábitos é o primeiro passo. O segundo é colocar esse conhecimento em prática. Seu cérebro está pronto para mudar – você só precisa dar as instruções certas.
              </p>
            </section>

            <CTACard variant="secondary" />
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CienciaHabitos;
